// Visit The Stimulus Handbook for more details
// https://stimulusjs.org/handbook/introduction
//
// This example controller works with specially annotated HTML like:
//
// <div data-controller="modal" data-modal-allow-background-close="false">
//   <a href="#" data-action="click->modal#open" class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded">
//     <span>Open Modal</span>
//   </a>
//
//   <!-- Modal Container -->
//   <div data-modal-target="container" data-action="click->modal#closeBackground keyup@window->modal#closeWithKeyboard" class="hidden animated fadeIn fixed inset-0 overflow-y-auto flex items-center justify-center" style="z-index: 9999;">
//     <!-- Modal Inner Container -->
//     <div class="max-h-screen w-full max-w-lg relative">
//       <!-- Modal Card -->
//       <div class="m-1 bg-white rounded shadow">
//         <div class="p-8">
//           <h2 class="text-xl mb-4">Large Modal Content</h2>
//           <p class="mb-4">This is an example modal dialog box.</p>
//
//           <div class="flex justify-end items-center flex-wrap mt-6">
//             <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" data-action="click->modal#close">Close</button>
//           </div>
//         </div>
//       </div>
//     </div>
//   </div>
// </div>

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['container']

  static values = {
    backdropColor: { type: String, default: 'rgba(0, 0, 0, 0.8)' },
    restoreScroll: { type: Boolean, default: true },
  }

  declare readonly containerTarget: HTMLElement
  declare readonly backdropColorValue: string
  declare readonly restoreScrollValue: boolean

  declare toggleClass: string
  declare backgroundId: string
  declare backgroundHtml: string
  declare allowBackgroundClose: boolean
  declare preventDefaultActionOpening: boolean
  declare preventDefaultActionClosing: boolean
  declare background: Element | null
  declare scrollPosition: number | undefined

  connect() {
    // Stimulus data.get() returns "" for value-less attributes, not null.
    // || is intentional: empty string should fall through to the default.
    /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
    this.toggleClass = this.data.get('class') || 'hidden'
    this.backgroundId = this.data.get('backgroundId') || 'modal-background'
    this.backgroundHtml =
      this.data.get('backgroundHtml') || this._backgroundHTML()
    this.allowBackgroundClose =
      (this.data.get('allowBackgroundClose') || 'true') === 'true'
    this.preventDefaultActionOpening =
      (this.data.get('preventDefaultActionOpening') || 'true') === 'true'
    this.preventDefaultActionClosing =
      (this.data.get('preventDefaultActionClosing') || 'true') === 'true'
    /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
  }

  disconnect() {
    this.close()
  }

  open(e: Event) {
    if (this.preventDefaultActionOpening) {
      e.preventDefault()
    }

    const target = e.target as HTMLElement | null
    if (target?.blur) {
      target.blur()
    }

    this.lockScroll()
    this.containerTarget.classList.remove(this.toggleClass)

    if (!this.data.get('disable-backdrop')) {
      document.body.insertAdjacentHTML('beforeend', this.backgroundHtml)
      this.background = document.querySelector(`#${this.backgroundId}`)
    }
  }

  close(e?: Event) {
    if (e && this.preventDefaultActionClosing) {
      e.preventDefault()
    }

    this.unlockScroll()
    this.containerTarget.classList.add(this.toggleClass)

    if (this.background) {
      this.background.remove()
    }
  }

  closeBackground(e: Event) {
    if (this.allowBackgroundClose && e.target === this.containerTarget) {
      this.close(e)
    }
  }

  closeWithKeyboard(e: KeyboardEvent) {
    if (
      e.key === 'Escape' &&
      !this.containerTarget.classList.contains(this.toggleClass)
    ) {
      this.close(e)
    }
  }

  _backgroundHTML() {
    return `<div id="${this.backgroundId}" class="fixed top-0 left-0 w-full h-full" style="background-color: ${this.backdropColorValue}; z-index: 9998;"></div>`
  }

  lockScroll() {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth
    document.body.style.paddingRight = `${scrollbarWidth}px`
    document.body.classList.add('fixed', 'inset-x-0', 'overflow-hidden')

    if (this.restoreScrollValue) {
      this.saveScrollPosition()
      document.body.style.top = `-${this.scrollPosition ?? 0}px`
    }
  }

  unlockScroll() {
    document.body.style.paddingRight = ''
    document.body.classList.remove('fixed', 'inset-x-0', 'overflow-hidden')

    if (this.restoreScrollValue) {
      this.restoreScrollPosition()
      document.body.style.top = ''
    }
  }

  saveScrollPosition() {
    this.scrollPosition = window.scrollY
  }

  restoreScrollPosition() {
    if (this.scrollPosition === undefined) return
    document.documentElement.scrollTop = this.scrollPosition
  }
}
